import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  ResultDialog,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import {
  liquidityProviderApi,
  providerAccountApi,
} from "@/services/api";
import {
  DollarSign,
  Loader2,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Coins,
  ArrowDownLeft,
  ArrowUpRight,
  TrendingUp,
  TrendingDown,
  Settings,
  History,
  Plus,
  Minus,
  Clock,
  ChevronUp,
  ChevronDown,
  Trash2,
  Zap,
  Shield,
  Pencil,
} from "lucide-react";
import { formatDate, formatCurrency } from "@/lib/utils";

// ============ Types ============

interface ProviderConfig {
  id: string;
  providerCode: string;
  name: string;
  baseUrl: string;
  authUrl?: string | null;
  organizationId?: string | null;
  environment: string;
  defaultSpreadPercentage?: string | null;
  minOrderAmountCents?: string | null;
  maxOrderAmountCents?: string | null;
  isActive: boolean | null;
  lastTestedAt: string | null;
  lastTestStatus: string | null;
  lastTestError: string | null;
  hasApiKey: boolean;
  hasApiSecret: boolean;
}

interface ProviderAccountSummary {
  providerCode: string;
  displayName: string;
  brlBalance: number;
  brlBalanceFormatted: string;
  usdBalance: number;
  usdBalanceFormatted: string;
  lastSyncAt?: string;
  syncStatus?: string;
  alerts: { lowBrlBalance: boolean; lowUsdBalance: boolean };
}

interface Rule {
  id: string;
  stablecoin: string;
  providerCode: string;
  priority: number;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Movement {
  id: string;
  providerCode: string;
  type: string;
  brlAmount: number;
  usdAmount: number;
  brlBalanceAfter: number;
  usdBalanceAfter: number;
  externalReference?: string;
  description?: string;
  tradeId?: string;
  currencyConversionId?: string;
  createdAt: string;
  createdByName?: string;
}

const MOVEMENT_TYPE_CONFIG: Record<string, {
  label: string;
  icon: typeof ArrowDownLeft;
  color: string;
}> = {
  DEPOSIT: { label: "Depósito", icon: ArrowDownLeft, color: "text-green-500" },
  WITHDRAWAL: { label: "Saque", icon: ArrowUpRight, color: "text-red-500" },
  TRADE_BUY: { label: "Compra USD", icon: TrendingUp, color: "text-blue-500" },
  TRADE_SELL: { label: "Venda USD", icon: TrendingDown, color: "text-purple-500" },
  FEE: { label: "Taxa", icon: Minus, color: "text-orange-500" },
  ADJUSTMENT: { label: "Ajuste", icon: Settings, color: "text-yellow-500" },
};

const PROVIDER_DISPLAY: Record<string, string> = {
  cainvest: "CAInvest",
  foxbit: "Foxbit OTC",
  "foxbit-book": "Foxbit Order Book",
  ripple: "Ripple",
  mock: "Mock (Simulação)",
};

type TabType = "providers" | "rules" | "account";

export function ProvidersPage() {
  const [activeTab, setActiveTab] = useState<TabType>("providers");

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<DollarSign className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Provedores USD"
        description="Gestão de provedores de liquidez, regras e contas correntes"
      />

      {/* Tabs */}
      <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "providers"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("providers")}
        >
          Provedores
        </button>
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "rules"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("rules")}
        >
          Regras
        </button>
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "account"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("account")}
        >
          Conta Corrente
        </button>
      </div>

      {activeTab === "providers" && <ProvidersTab />}
      {activeTab === "rules" && <RulesTab />}
      {activeTab === "account" && <AccountTab />}
    </div>
  );
}

// ============ Aba 1: Provedores ============

function ProvidersTab() {
  const [configs, setConfigs] = useState<ProviderConfig[]>([]);
  const [accounts, setAccounts] = useState<ProviderAccountSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [testingCode, setTestingCode] = useState<string | null>(null);
  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
  }>({ open: false, variant: "success", title: "" });

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    providerCode: "",
    name: "",
    baseUrl: "",
    apiKey: "",
    apiSecret: "",
    environment: "production" as "sandbox" | "production",
    isActive: true,
  });
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [configsData, accountsData] = await Promise.all([
        liquidityProviderApi.listConfigs(),
        providerAccountApi.listProviderAccounts(),
      ]);
      setConfigs(configsData || []);
      setAccounts(accountsData || []);
    } catch (error) {
      console.error("Erro ao carregar provedores:", error);
    } finally {
      setLoading(false);
    }
  }

  function handleOpenEdit(config: ProviderConfig) {
    setEditForm({
      providerCode: config.providerCode,
      name: config.name,
      baseUrl: config.baseUrl,
      apiKey: "",
      apiSecret: "",
      environment: (config.environment as "sandbox" | "production") || "production",
      isActive: config.isActive ?? true,
    });
    setEditDialogOpen(true);
  }

  async function handleSaveEdit() {
    setEditSaving(true);
    try {
      await liquidityProviderApi.upsertConfig({
        providerCode: editForm.providerCode,
        name: editForm.name,
        baseUrl: editForm.baseUrl,
        apiKey: editForm.apiKey || undefined,
        apiSecret: editForm.apiSecret || undefined,
        environment: editForm.environment,
        isActive: editForm.isActive,
      });
      setEditDialogOpen(false);
      await loadData();
      await liquidityProviderApi.reload();
      setResultDialog({
        open: true,
        variant: "success",
        title: "Provedor atualizado",
        description: `As configurações de ${PROVIDER_DISPLAY[editForm.providerCode] || editForm.providerCode} foram salvas.`,
      });
    } catch (error) {
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao salvar",
        description: "Não foi possível atualizar as configurações do provedor.",
      });
    } finally {
      setEditSaving(false);
    }
  }

  async function handleTest(code: string) {
    setTestingCode(code);
    try {
      const result = await liquidityProviderApi.testConnection(code);
      setResultDialog({
        open: true,
        variant: result.success ? "success" : "error",
        title: result.success ? "Conexão OK" : "Falha na conexão",
        description: result.message,
      });
      await loadData();
    } catch (error) {
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao testar",
        description: "Não foi possível testar a conexão.",
      });
    } finally {
      setTestingCode(null);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {configs.map((config) => {
          const account = accounts.find((a) => a.providerCode === config.providerCode);
          return (
            <Card key={config.id} className={!config.isActive ? "opacity-60" : ""}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle className="text-base">{config.name}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">{config.providerCode}</p>
                </div>
                <Badge variant={config.isActive ? "success" : "secondary"}>
                  {config.isActive ? "Ativo" : "Inativo"}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Ambiente */}
                <div className="flex items-center gap-2">
                  <Badge variant={config.environment === "production" ? "default" : "warning"} className="text-xs">
                    {config.environment === "production" ? "Produção" : "Sandbox"}
                  </Badge>
                  {config.hasApiKey && (
                    <Badge variant="secondary" className="text-xs">
                      <Shield className="w-3 h-3 mr-1" />
                      Credenciais OK
                    </Badge>
                  )}
                </div>

                {/* Saldos */}
                {account && (
                  <div className="bg-muted rounded-lg p-3 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">BRL</span>
                      <span className={`font-semibold ${account.alerts?.lowBrlBalance ? "text-destructive" : ""}`}>
                        {account.brlBalanceFormatted}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">USD</span>
                      <span className={`font-semibold ${account.alerts?.lowUsdBalance ? "text-destructive" : ""}`}>
                        {account.usdBalanceFormatted}
                      </span>
                    </div>
                    {(account.alerts?.lowBrlBalance || account.alerts?.lowUsdBalance) && (
                      <div className="flex items-center gap-1 text-destructive pt-1">
                        <AlertTriangle className="w-3 h-3" />
                        <span className="text-xs">Saldo baixo</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Último teste */}
                {config.lastTestedAt && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    {config.lastTestStatus === "OK" ? (
                      <CheckCircle className="w-3 h-3 text-green-500" />
                    ) : (
                      <XCircle className="w-3 h-3 text-red-500" />
                    )}
                    Testado em {formatDate(config.lastTestedAt)}
                  </div>
                )}

                {/* Ações */}
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleTest(config.providerCode)}
                    disabled={testingCode === config.providerCode}
                  >
                    {testingCode === config.providerCode ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Zap className="w-3 h-3 mr-1" />
                    )}
                    Testar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenEdit(config)}
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Provider Dialog */}
      {editDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader className="flex flex-row items-start gap-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-primary/10">
                <Settings className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg">
                  Editar Provedor — {PROVIDER_DISPLAY[editForm.providerCode] || editForm.providerCode}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Atualize as configurações e credenciais. Deixe API Key e Secret em branco para manter os valores atuais.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditDialogOpen(false)}
                disabled={editSaving}
                className="flex-shrink-0 -mt-1 -mr-2"
              >
                <XCircle className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Nome do provedor"
                />
              </div>
              <div className="space-y-2">
                <Label>Base URL</Label>
                <Input
                  value={editForm.baseUrl}
                  onChange={(e) => setEditForm({ ...editForm, baseUrl: e.target.value })}
                  placeholder="https://api.exemplo.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    value={editForm.apiKey}
                    onChange={(e) => setEditForm({ ...editForm, apiKey: e.target.value })}
                    placeholder="Manter atual"
                    type="password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Secret</Label>
                  <Input
                    value={editForm.apiSecret}
                    onChange={(e) => setEditForm({ ...editForm, apiSecret: e.target.value })}
                    placeholder="Manter atual"
                    type="password"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">
                Deixe em branco para manter as credenciais atuais.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ambiente</Label>
                  <select
                    className="w-full h-9 rounded-md border border-neutral-200 bg-white px-3 py-1 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-400"
                    value={editForm.environment}
                    onChange={(e) => setEditForm({ ...editForm, environment: e.target.value as "sandbox" | "production" })}
                  >
                    <option value="production">Produção</option>
                    <option value="sandbox">Sandbox</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <select
                    className="w-full h-9 rounded-md border border-neutral-200 bg-white px-3 py-1 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-400"
                    value={editForm.isActive ? "active" : "inactive"}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value === "active" })}
                  >
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={editSaving}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveEdit} disabled={editSaving || !editForm.name || !editForm.baseUrl}>
                  {editSaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        variant={resultDialog.variant}
        title={resultDialog.title}
        description={resultDialog.description}
      />
    </>
  );
}

// ============ Aba 2: Regras ============

function RulesTab() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [configs, setConfigs] = useState<ProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
  }>({ open: false, variant: "success", title: "" });

  // Add rule form
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newRule, setNewRule] = useState({ providerCode: "", role: "PRIMARY" });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [rulesData, configsData] = await Promise.all([
        liquidityProviderApi.listRules(),
        liquidityProviderApi.listConfigs(),
      ]);
      setRules((rulesData || []).sort((a, b) => a.priority - b.priority));
      setConfigs(configsData || []);
    } catch (error) {
      console.error("Erro ao carregar regras:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleMoveUp(rule: Rule) {
    const idx = rules.findIndex((r) => r.id === rule.id);
    if (idx <= 0) return;

    const newRules = [...rules];
    [newRules[idx - 1], newRules[idx]] = [newRules[idx], newRules[idx - 1]];
    const reordered = newRules.map((r, i) => ({ id: r.id, priority: i + 1 }));

    setSaving(true);
    try {
      await liquidityProviderApi.reorderRules({ rules: reordered });
      await loadData();
      await liquidityProviderApi.reload();
    } catch (error) {
      console.error("Erro ao reordenar:", error);
    } finally {
      setSaving(false);
    }
  }

  async function handleMoveDown(rule: Rule) {
    const idx = rules.findIndex((r) => r.id === rule.id);
    if (idx >= rules.length - 1) return;

    const newRules = [...rules];
    [newRules[idx], newRules[idx + 1]] = [newRules[idx + 1], newRules[idx]];
    const reordered = newRules.map((r, i) => ({ id: r.id, priority: i + 1 }));

    setSaving(true);
    try {
      await liquidityProviderApi.reorderRules({ rules: reordered });
      await loadData();
      await liquidityProviderApi.reload();
    } catch (error) {
      console.error("Erro ao reordenar:", error);
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(rule: Rule) {
    setSaving(true);
    try {
      await liquidityProviderApi.updateRule(rule.id, { isActive: !rule.isActive });
      await loadData();
      await liquidityProviderApi.reload();
    } catch (error) {
      console.error("Erro ao atualizar regra:", error);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteRule(rule: Rule) {
    setSaving(true);
    try {
      await liquidityProviderApi.deleteRule(rule.id);
      await loadData();
      await liquidityProviderApi.reload();
    } catch (error) {
      console.error("Erro ao excluir regra:", error);
    } finally {
      setSaving(false);
    }
  }

  async function handleAddRule() {
    if (!newRule.providerCode) return;
    setSaving(true);
    try {
      await liquidityProviderApi.createRule({
        providerCode: newRule.providerCode,
        priority: rules.length + 1,
        role: newRule.role,
        isActive: true,
      });
      setAddDialogOpen(false);
      setNewRule({ providerCode: "", role: "PRIMARY" });
      await loadData();
      await liquidityProviderApi.reload();
      setResultDialog({
        open: true,
        variant: "success",
        title: "Regra adicionada",
        description: "A regra foi criada e o gateway foi recarregado.",
      });
    } catch (error) {
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao adicionar regra",
        description: "Não foi possível criar a regra. Verifique se já não existe.",
      });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const availableProviders = configs.filter(
    (c) => !rules.some((r) => r.providerCode === c.providerCode)
  );

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Configure a prioridade e fallback entre provedores. O provedor com menor prioridade (1) é usado primeiro.
          </p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)} disabled={availableProviders.length === 0}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Regra
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {rules.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma regra configurada</p>
              <p className="text-xs mt-1">Adicione regras para configurar o roteamento de provedores.</p>
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium w-20">Prioridade</th>
                    <th className="p-4 font-medium">Provedor</th>
                    <th className="p-4 font-medium">Stablecoin</th>
                    <th className="p-4 font-medium">Papel</th>
                    <th className="p-4 font-medium">Ativo</th>
                    <th className="p-4 font-medium w-32">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule, idx) => (
                    <tr key={rule.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-lg">{rule.priority}</span>
                          <div className="flex flex-col">
                            <button
                              className="p-0.5 hover:bg-muted rounded disabled:opacity-30"
                              onClick={() => handleMoveUp(rule)}
                              disabled={idx === 0 || saving}
                            >
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              className="p-0.5 hover:bg-muted rounded disabled:opacity-30"
                              onClick={() => handleMoveDown(rule)}
                              disabled={idx === rules.length - 1 || saving}
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="font-medium">
                          {PROVIDER_DISPLAY[rule.providerCode] || rule.providerCode}
                        </span>
                      </td>
                      <td className="p-4">
                        <Badge variant="secondary">{rule.stablecoin}</Badge>
                      </td>
                      <td className="p-4">
                        <Badge variant={rule.role === "PRIMARY" ? "default" : "warning"}>
                          {rule.role === "PRIMARY" ? "Primário" : rule.role === "FALLBACK" ? "Fallback" : rule.role}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => handleToggleActive(rule)}
                          disabled={saving}
                          className="cursor-pointer"
                        >
                          <Badge variant={rule.isActive ? "success" : "secondary"}>
                            {rule.isActive ? "Sim" : "Não"}
                          </Badge>
                        </button>
                      </td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRule(rule)}
                          disabled={saving}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Rule Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Regra</DialogTitle>
            <DialogDescription>
              Selecione um provedor e configure seu papel no roteamento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Provedor</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={newRule.providerCode}
                onChange={(e) => setNewRule({ ...newRule, providerCode: e.target.value })}
              >
                <option value="">Selecione...</option>
                {availableProviders.map((c) => (
                  <option key={c.providerCode} value={c.providerCode}>
                    {c.name} ({c.providerCode})
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Papel</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={newRule.role}
                onChange={(e) => setNewRule({ ...newRule, role: e.target.value })}
              >
                <option value="PRIMARY">Primário</option>
                <option value="FALLBACK">Fallback</option>
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddRule} disabled={saving || !newRule.providerCode}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Adicionar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        variant={resultDialog.variant}
        title={resultDialog.title}
        description={resultDialog.description}
      />
    </>
  );
}

// ============ Aba 3: Conta Corrente ============

function AccountTab() {
  const [accounts, setAccounts] = useState<ProviderAccountSummary[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [summary, setSummary] = useState<ProviderAccountSummary | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Modals
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [withdrawalModalOpen, setWithdrawalModalOpen] = useState(false);
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);

  // Form states
  const [depositForm, setDepositForm] = useState({ brlAmount: "", usdAmount: "", description: "" });
  const [withdrawalForm, setWithdrawalForm] = useState({ brlAmount: "", usdAmount: "", description: "" });
  const [adjustmentForm, setAdjustmentForm] = useState({ brlAmount: "", usdAmount: "", description: "" });
  const [formLoading, setFormLoading] = useState(false);

  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
  }>({ open: false, variant: "success", title: "" });

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    if (selectedProvider) {
      loadProviderData();
    }
  }, [selectedProvider, typeFilter, page]);

  async function loadAccounts() {
    setLoading(true);
    try {
      const data = await providerAccountApi.listProviderAccounts();
      setAccounts(data || []);
      if (data && data.length > 0 && !selectedProvider) {
        setSelectedProvider(data[0].providerCode);
      }
    } catch (error) {
      console.error("Erro ao carregar contas:", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadProviderData() {
    if (!selectedProvider) return;
    try {
      const [summaryData, movementsData] = await Promise.all([
        providerAccountApi.getSummary(selectedProvider),
        providerAccountApi.getMovements(selectedProvider, {
          type: typeFilter || undefined,
          page,
          limit: 20,
        }),
      ]);
      setSummary(summaryData);
      setMovements(movementsData?.items || []);
      setTotal(movementsData?.total || 0);
      setTotalPages(movementsData?.totalPages || 1);
    } catch (error) {
      console.error("Erro ao carregar dados do provedor:", error);
    }
  }

  async function handleSync() {
    if (!selectedProvider) return;
    setSyncing(true);
    try {
      const result = await providerAccountApi.syncWithApi(selectedProvider);
      setResultDialog({
        open: true,
        variant: result.success ? "success" : "error",
        title: result.success ? "Sincronização concluída" : "Erro na sincronização",
        description: result.success
          ? `BRL: ${formatCurrency((result.apiBrlBalance || 0) / 100)} | USD: $${((result.apiUsdBalance || 0) / 100).toFixed(2)}`
          : result.error || "Não foi possível sincronizar.",
      });
      await loadProviderData();
    } catch (error) {
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao sincronizar",
        description: "Não foi possível sincronizar com a API do provedor.",
      });
    } finally {
      setSyncing(false);
    }
  }

  async function handleDeposit() {
    if (!selectedProvider) return;
    setFormLoading(true);
    try {
      await providerAccountApi.recordDeposit(selectedProvider, {
        brlAmount: depositForm.brlAmount ? Math.round(parseFloat(depositForm.brlAmount) * 100) : undefined,
        usdAmount: depositForm.usdAmount ? Math.round(parseFloat(depositForm.usdAmount) * 100) : undefined,
        description: depositForm.description || undefined,
      });
      setDepositModalOpen(false);
      setDepositForm({ brlAmount: "", usdAmount: "", description: "" });
      setResultDialog({ open: true, variant: "success", title: "Depósito registrado" });
      await loadProviderData();
    } catch (error) {
      setResultDialog({ open: true, variant: "error", title: "Erro ao registrar depósito" });
    } finally {
      setFormLoading(false);
    }
  }

  async function handleWithdrawal() {
    if (!selectedProvider) return;
    setFormLoading(true);
    try {
      await providerAccountApi.recordWithdrawal(selectedProvider, {
        brlAmount: withdrawalForm.brlAmount ? Math.round(parseFloat(withdrawalForm.brlAmount) * 100) : undefined,
        usdAmount: withdrawalForm.usdAmount ? Math.round(parseFloat(withdrawalForm.usdAmount) * 100) : undefined,
        description: withdrawalForm.description || undefined,
      });
      setWithdrawalModalOpen(false);
      setWithdrawalForm({ brlAmount: "", usdAmount: "", description: "" });
      setResultDialog({ open: true, variant: "success", title: "Saque registrado" });
      await loadProviderData();
    } catch (error) {
      setResultDialog({ open: true, variant: "error", title: "Erro ao registrar saque" });
    } finally {
      setFormLoading(false);
    }
  }

  async function handleAdjustment() {
    if (!selectedProvider || !adjustmentForm.description) return;
    setFormLoading(true);
    try {
      await providerAccountApi.recordAdjustment(selectedProvider, {
        brlAmount: adjustmentForm.brlAmount ? Math.round(parseFloat(adjustmentForm.brlAmount) * 100) : undefined,
        usdAmount: adjustmentForm.usdAmount ? Math.round(parseFloat(adjustmentForm.usdAmount) * 100) : undefined,
        description: adjustmentForm.description,
      });
      setAdjustmentModalOpen(false);
      setAdjustmentForm({ brlAmount: "", usdAmount: "", description: "" });
      setResultDialog({ open: true, variant: "success", title: "Ajuste registrado" });
      await loadProviderData();
    } catch (error) {
      setResultDialog({ open: true, variant: "error", title: "Erro ao registrar ajuste" });
    } finally {
      setFormLoading(false);
    }
  }

  function getMovementIcon(type: string) {
    const config = MOVEMENT_TYPE_CONFIG[type] || MOVEMENT_TYPE_CONFIG.ADJUSTMENT;
    const Icon = config.icon;
    return <Icon className={`w-4 h-4 ${config.color}`} />;
  }

  function getMovementLabel(type: string) {
    return MOVEMENT_TYPE_CONFIG[type]?.label || type;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      {/* Provider Selector */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Label className="whitespace-nowrap">Provedor:</Label>
          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[200px]"
            value={selectedProvider}
            onChange={(e) => {
              setSelectedProvider(e.target.value);
              setPage(1);
              setTypeFilter("");
            }}
          >
            {accounts.map((a) => (
              <option key={a.providerCode} value={a.providerCode}>
                {a.displayName} ({a.providerCode})
              </option>
            ))}
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing || !selectedProvider}>
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
          Sincronizar API
        </Button>
      </div>

      {summary && (
        <>
          {/* Balance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className={summary.alerts?.lowBrlBalance ? "border-destructive bg-destructive/5" : ""}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Saldo BRL</CardTitle>
                <Coins className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.brlBalanceFormatted}</div>
                {summary.alerts?.lowBrlBalance && (
                  <div className="flex items-center gap-1 mt-2 text-destructive">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-xs font-medium">Saldo abaixo do mínimo!</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className={summary.alerts?.lowUsdBalance ? "border-destructive bg-destructive/5" : ""}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Saldo USD</CardTitle>
                <DollarSign className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.usdBalanceFormatted}</div>
                {summary.alerts?.lowUsdBalance && (
                  <div className="flex items-center gap-1 mt-2 text-destructive">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-xs font-medium">Saldo abaixo do mínimo!</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sync Info */}
          {summary.lastSyncAt && (
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Última Sincronização</p>
                      <p className="text-sm text-muted-foreground">{formatDate(summary.lastSyncAt)}</p>
                    </div>
                  </div>
                  {summary.syncStatus && (
                    <Badge variant={summary.syncStatus === "OK" ? "success" : "warning"}>
                      {summary.syncStatus}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => setDepositModalOpen(true)} disabled={!selectedProvider}>
          <Plus className="w-4 h-4 mr-2" />
          Registrar Depósito
        </Button>
        <Button variant="outline" onClick={() => setWithdrawalModalOpen(true)} disabled={!selectedProvider}>
          <Minus className="w-4 h-4 mr-2" />
          Registrar Saque
        </Button>
        <Button variant="outline" onClick={() => setAdjustmentModalOpen(true)} disabled={!selectedProvider}>
          <Settings className="w-4 h-4 mr-2" />
          Registrar Ajuste
        </Button>
      </div>

      {/* Movements Filter */}
      <div className="flex gap-2 flex-wrap">
        {["", "DEPOSIT", "WITHDRAWAL", "TRADE_BUY", "TRADE_SELL", "ADJUSTMENT"].map((type) => (
          <Button
            key={type}
            variant={typeFilter === type ? "default" : "outline"}
            size="sm"
            onClick={() => { setTypeFilter(type); setPage(1); }}
          >
            {type === "" ? "Todos" : MOVEMENT_TYPE_CONFIG[type]?.label || type}
          </Button>
        ))}
      </div>

      {/* Movements Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Movimentações
            <span className="text-sm font-normal text-muted-foreground ml-auto">
              {movements.length} de {total}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {movements.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma movimentação encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Data</th>
                    <th className="p-4 font-medium">Tipo</th>
                    <th className="p-4 font-medium">BRL</th>
                    <th className="p-4 font-medium">USD</th>
                    <th className="p-4 font-medium">Saldo Após</th>
                    <th className="p-4 font-medium">Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((movement) => (
                    <tr key={movement.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                      <td className="p-4">
                        <p className="font-medium">{formatDate(movement.createdAt)}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(movement.createdAt).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {getMovementIcon(movement.type)}
                          <span>{getMovementLabel(movement.type)}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        {movement.brlAmount !== 0 && (
                          <span className={movement.brlAmount > 0 ? "text-green-500" : "text-red-500"}>
                            {movement.brlAmount > 0 ? "+" : ""}{formatCurrency(movement.brlAmount / 100)}
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        {movement.usdAmount !== 0 && (
                          <span className={movement.usdAmount > 0 ? "text-green-500" : "text-red-500"}>
                            {movement.usdAmount > 0 ? "+" : ""}$ {(movement.usdAmount / 100).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <p className="text-xs">BRL: {formatCurrency(movement.brlBalanceAfter / 100)}</p>
                        <p className="text-xs">USD: $ {(movement.usdBalanceAfter / 100).toFixed(2)}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                          {movement.description || movement.externalReference || "-"}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-4 border-t border-border">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground">
                Página {page} de {totalPages}
              </span>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                Próxima
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deposit Modal */}
      <Dialog open={depositModalOpen} onOpenChange={setDepositModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Depósito</DialogTitle>
            <DialogDescription>
              Registre um depósito na conta do provedor {PROVIDER_DISPLAY[selectedProvider] || selectedProvider}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Valor BRL</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0,00"
                value={depositForm.brlAmount}
                onChange={(e) => setDepositForm({ ...depositForm, brlAmount: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Valor USD</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={depositForm.usdAmount}
                onChange={(e) => setDepositForm({ ...depositForm, usdAmount: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição (opcional)</Label>
              <Input
                placeholder="Descrição do depósito"
                value={depositForm.description}
                onChange={(e) => setDepositForm({ ...depositForm, description: e.target.value })}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDepositModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleDeposit} disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Registrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdrawal Modal */}
      <Dialog open={withdrawalModalOpen} onOpenChange={setWithdrawalModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Saque</DialogTitle>
            <DialogDescription>
              Registre um saque da conta do provedor {PROVIDER_DISPLAY[selectedProvider] || selectedProvider}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Valor BRL</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0,00"
                value={withdrawalForm.brlAmount}
                onChange={(e) => setWithdrawalForm({ ...withdrawalForm, brlAmount: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Valor USD</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={withdrawalForm.usdAmount}
                onChange={(e) => setWithdrawalForm({ ...withdrawalForm, usdAmount: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição (opcional)</Label>
              <Input
                placeholder="Descrição do saque"
                value={withdrawalForm.description}
                onChange={(e) => setWithdrawalForm({ ...withdrawalForm, description: e.target.value })}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setWithdrawalModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleWithdrawal} disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Registrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjustment Modal */}
      <Dialog open={adjustmentModalOpen} onOpenChange={setAdjustmentModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Ajuste</DialogTitle>
            <DialogDescription>
              Registre um ajuste manual no saldo. Valores positivos aumentam, negativos diminuem.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Ajuste BRL (pode ser negativo)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0,00"
                value={adjustmentForm.brlAmount}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, brlAmount: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Ajuste USD (pode ser negativo)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={adjustmentForm.usdAmount}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, usdAmount: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição (obrigatório)</Label>
              <Input
                placeholder="Motivo do ajuste"
                value={adjustmentForm.description}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, description: e.target.value })}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setAdjustmentModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleAdjustment} disabled={formLoading || !adjustmentForm.description}>
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Registrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        variant={resultDialog.variant}
        title={resultDialog.title}
        description={resultDialog.description}
      />
    </>
  );
}
